import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Stack,
  Text,
  TextField,
  Dropdown,
  DatePicker,
  DayOfWeek,
  Toggle,
  PrimaryButton,
  DefaultButton,
  Separator,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IconButton,
  Persona,
} from "@fluentui/react";
import { Chip } from "./ProposalDashboard";

/**
 * CreateProjectPage.jsx — Dual‑list pickers for People & Tags
 * Fluent UI v8, axios
 *
 * API contract (adjust to your backend):
 *  - GET  /api/clients?q=                       -> [{id,name}]
 *  - GET  /api/people                           -> [{id, display_name, title, email}]
 *  - GET  /api/tags                              -> [{id, name, slug}]
 *  - GET  /api/proposals?q=&limit=50            -> [{id, title, client_name, date_submitted}]
 *  - POST /api/compass_projects                 -> {id, ...}
 *  - POST /api/compass_projects/:id/members     -> body: {people: [uuid]}
 *  - POST /api/compass_projects/:id/tags        -> body: {tags: [uuid]}
 *  - POST /api/proposal_project_link            -> body: {project_id, proposal_ids:[uuid]}
 */

const API_BASE = process.env.REACT_APP_API_BASE || "";

const dayPickerStrings = {
  months: [
    "January","February","March","April","May","June","July","August","September","October","November","December",
  ],
  shortMonths: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
  days: ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
  shortDays: ["S","M","T","W","T","F","S"],
  goToToday: "Go to today",
};

const REQUIRED = (v) => (v === undefined || v === null || String(v).trim() === "" ? "Required" : undefined);

// ---------- Small bits ----------
function SectionHeader({ title, sub }) {
  return (
    <Stack tokens={{ childrenGap: 4 }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>{title}</Text>
      {sub && <Text variant="small" styles={{ root: { color: "#666" } }}>{sub}</Text>}
    </Stack>
  );
}

function Pill({ text, onRemove, ariaLabel }) {
  return (
    <Stack horizontal verticalAlign="center" styles={{ root: { border: "1px solid #ddd", padding: "2px 6px", borderRadius: 12 } }}>
      <Text variant="small">{text}</Text>
      {onRemove && (
        <IconButton ariaLabel={ariaLabel || `Remove ${text}`} iconProps={{ iconName: "Cancel" }} styles={{ root: { marginLeft: 4, height: 24, width: 24 } }} onClick={onRemove} />
      )}
    </Stack>
  );
}

// ---------- Dual‑list People Picker ----------
function PeopleDualList({ selected, setSelected }) {
  const [allPeople, setAllPeople] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/people`);
        if (!alive) return;
        setAllPeople(res.data.data.filter(el => el.active) || []);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  const selectedIds = new Set(selected.map(p => p.id));

  const available = (allPeople || []).filter(p => !selectedIds.has(p.id) && (
    !filter || (p.display_name?.toLowerCase().includes(filter.toLowerCase()) || p.email?.toLowerCase().includes(filter.toLowerCase()) || p.title?.toLowerCase().includes(filter.toLowerCase()))
  ));

  console.log({available, selected});

  return (
    <Stack horizontal tokens={{ childrenGap: 24 }} wrap>
      {/* Left: Selected */}
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { minWidth: 360, flex: 1 } }}>
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Selected people</Text>
        <Stack tokens={{childrenGap: 8}} wrap horizontal>
          {
            selected.map(person => {
              return <Stack.Item styles={{root: {display: 'flex'}}}>
                <Persona key={person.id} text={person.name} secondaryText={person.title} imageUrl={person.image_url}  />
                <IconButton iconProps={{iconName: "SkypeCircleMinus"}} onClick={() => setSelected(selected.filter(p => p.id !== person.id))} />
              </Stack.Item>
            })
          }
        </Stack>
        {/* <DetailsList
          items={selected}
          columns={[
            { key: "name", name: "Name", fieldName: "name", minWidth: 180 },
            { key: "title", name: "Title", fieldName: "title", minWidth: 140 },
            { key: "remove", name: "", minWidth: 60, onRender: (item) => <DefaultButton text="Remove" onClick={() => setSelected(selected.filter(p => p.id !== item.id))} /> },
          ]}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
        /> */}
        {selected.length === 0 && <Text variant="small" styles={{ root: { color: "#666" } }}>No people selected.</Text>}
      </Stack>

      {/* Right: Available */}
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { minWidth: 360, flex: 1 } }}>
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Available people</Text>
        {/* <TextField placeholder="Filter by name, email, title" value={filter} onChange={(_, v) => setFilter(v || "")} /> */}
        <Stack tokens={{childrenGap: 8}} wrap horizontal>
          {
            available.map(person => {
              return <Stack.Item styles={{root: {display: 'flex'}}}>
                <Persona key={person.id} text={person.name} secondaryText={person.title} imageUrl={person.image_url}  />
                <IconButton iconProps={{iconName: "CircleAdditionSolid"}} onClick={() => setSelected([...selected, person])} />
              </Stack.Item>
            })
          }
        </Stack>
        {/* <DetailsList
          items={available}
          columns={[
            { key: "name", name: "Name", fieldName: "name", minWidth: 180 },
            { key: "title", name: "Title", fieldName: "title", minWidth: 140 },
            // { key: "email", name: "Email", fieldName: "email", minWidth: 180 },
            { key: "add", name: "", minWidth: 60, onRender: (item) => <PrimaryButton text="Add" onClick={() => setSelected([...selected, item])} /> },
          ]}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
        /> */}
      </Stack>
    </Stack>
  );
}

// ---------- Dual‑list Tags Picker ----------
function TagsDualList({ selected, setSelected }) {
  const [allTags, setAllTags] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/tags`);
        if (!alive) return;
        setAllTags(res.data || []);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  const selectedIds = new Set(selected.map(t => t.id));
  const available = (allTags || []).filter(t => !selectedIds.has(t.id) && (!filter || t.name?.toLowerCase().includes(filter.toLowerCase()) || t.slug?.toLowerCase().includes(filter.toLowerCase())));

  return (
    <Stack horizontal tokens={{ childrenGap: 24 }} wrap>
      {/* Left: Selected */}
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { minWidth: 360, flex: 1 } }}>
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Selected tags</Text>
        <Stack tokens={{childrenGap: 16}} wrap horizontal>
          {
            selected.map(tag => {
              return <Chip primary onClick={() => setSelected(selected.filter(t => t.id !== tag.id))}>
                <span>{tag.name}</span>
                <span style={{marginLeft: 8}}>-</span>
              </Chip>
            })
          }
        </Stack>
        {/* <DetailsList
          items={selected}
          columns={[
            { key: "name", name: "Name", fieldName: "name", minWidth: 220 },
            { key: "slug", name: "Slug", fieldName: "slug", minWidth: 180 },
            { key: "remove", name: "", minWidth: 60, onRender: (item) => <DefaultButton text="Remove" onClick={() => setSelected(selected.filter(t => t.id !== item.id))} /> },
          ]}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
        /> */}
        {selected.length === 0 && <Text variant="small" styles={{ root: { color: "#666" } }}>No tags selected.</Text>}
      </Stack>

      {/* Right: Available */}
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { minWidth: 360, flex: 1 } }}>
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Available tags</Text>
        <Stack tokens={{childrenGap: 16}} wrap horizontal>
          {
            available.map(tag => {
              return <Chip onClick={() => setSelected([...selected, tag])}>
                <span>{tag.name}</span>
                <span style={{marginLeft: 8}}>+</span>
              </Chip>
            })
          }
        </Stack>
        {/* <TextField placeholder="Filter by name or slug" value={filter} onChange={(_, v) => setFilter(v || "")} /> */}
        {/* <DetailsList
          items={available}
          columns={[
            { key: "name", name: "Name", fieldName: "name", minWidth: 220 },
            { key: "slug", name: "Slug", fieldName: "slug", minWidth: 180 },
            { key: "add", name: "", minWidth: 60, onRender: (item) => <PrimaryButton text="Add" onClick={() => setSelected([...selected, item])} /> },
          ]}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
        /> */}
      </Stack>
    </Stack>
  );
}

// ---------- Proposal search/linker (unchanged) ----------
function ProposalLinker({ selected, onAdd, onRemove }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    let alive = true;
    async function search() {
      try {
        const res = await axios.get(`${API_BASE}/api/proposals`, { params: { q: query, limit: 50 } });
        if (!alive) return;
        setResults(res.data || []);
      } catch {}
    }
    if (isOpen && (query?.length ?? 0) >= 2) search(); else if (!isOpen) setResults([]);
    return () => { alive = false; };
  }, [query, isOpen]);

  return (
    <>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Text variant="smallPlus" styles={{ root: { fontWeight: 600 } }}>Linked proposals</Text>
        <DefaultButton text="Add proposals" onClick={() => setIsOpen(true)} />
      </Stack>
      <Stack horizontal wrap tokens={{ childrenGap: 6 }}>
        {selected.map((p) => (
          <Pill key={p.id} text={p.title} onRemove={() => onRemove(p.id)} />
        ))}
        {selected.length === 0 && <Text variant="small" styles={{ root: { color: "#666" } }}>No proposals linked.</Text>}
      </Stack>

      <Panel isOpen={isOpen} onDismiss={() => setIsOpen(false)} headerText="Find proposals" type={PanelType.medium} closeButtonAriaLabel="Close">
        <Stack tokens={{ childrenGap: 12 }}>
          <TextField label="Search proposals" value={query} onChange={(_, v) => setQuery(v || "")} placeholder="Type at least 2 characters" />
          <DetailsList
            items={results}
            columns={[
              { key: "title", name: "Title", fieldName: "title", minWidth: 240 },
              { key: "client", name: "Client", fieldName: "client_name", minWidth: 160 },
              { key: "date", name: "Submitted", fieldName: "date_submitted", minWidth: 120 },
              { key: "add", name: "", minWidth: 60, onRender: (item) => <PrimaryButton text="Add" onClick={() => onAdd(item)} /> },
            ]}
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
          />
        </Stack>
      </Panel>
    </>
  );
}

// ---------- Main Page ----------
export default function CreateProject() {
  // Core form state
  const [title, setTitle] = useState("");
  const [projectNumber, setProjectNumber] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState("");

  const [status, setStatus] = useState("Active");
  const [billableAmount, setBillableAmount] = useState("");
  const [currency, setCurrency] = useState("CAD");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sharepointId, setSharepointId] = useState("");
  const [folderLink, setFolderLink] = useState("");
  const [compassPmRef, setCompassPmRef] = useState("");
  const [teamMembersRef, setTeamMembersRef] = useState("");
  const [clientFriendlyName, setClientFriendlyName] = useState("");
  const [canonicalDescription, setCanonicalDescription] = useState("");
  const [searchText, setSearchText] = useState("");
  const [hasFollowup, setHasFollowup] = useState(false);
  const [hasMultiplePhases, setHasMultiplePhases] = useState(false);
  const [clientRetention, setClientRetention] = useState(false);

  // Associations
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedProposals, setSelectedProposals] = useState([]);

  // UX state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Load clients (simple type‑ahead via separate input)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/clients`, { params: { q: clientSearch } });
        if (!alive) return;
        setClientOptions((res.data || []).map(c => ({ key: c.id, text: c.name })));
      } catch {}
    })();
    return () => { alive = false; };
  }, [clientSearch]);

  const statusOptions = useMemo(() => ([
    { key: "Active", text: "Active" },
    { key: "Completed", text: "Completed" },
    { key: "On Hold", text: "On Hold" },
    { key: "Cancelled", text: "Cancelled" },
  ]), []);

  const currencyOptions = useMemo(() => ([
    { key: "CAD", text: "CAD" },
    { key: "USD", text: "USD" },
    { key: "EUR", text: "EUR" },
  ]), []);

  // Validation
  const titleError = REQUIRED(title);
  const clientError = REQUIRED(clientId);
  const statusError = REQUIRED(status);
  const canSave = !titleError && !clientError && !statusError && !saving;

  // Save
  async function handleSave() {
    setSaving(true); setError(""); setSuccess("");
    try {
      // 1) Create project core
      const payload = {
        project_number: projectNumber || null,
        title,
        client_id: clientId,
        status,
        billable_amount: billableAmount ? Number(billableAmount) : null,
        currency,
        summary_start_date: startDate ? startDate.toISOString().substring(0, 10) : null,
        summary_end_date: endDate ? endDate.toISOString().substring(0, 10) : null,
        sharepoint_id: sharepointId || null,
        folder_link: folderLink || null,
        compass_pm_ref: compassPmRef || null,
        team_members_ref: teamMembersRef || null,
        client_friendly_name: clientFriendlyName || null,
        canonical_description: canonicalDescription || null,
        search_text: searchText || null,
        has_followup: hasFollowup,
        has_multiple_phases: hasMultiplePhases,
        client_retention: clientRetention,
      };

      const createRes = await axios.post(`${API_BASE}/api/compass_projects`, payload);
      const project = createRes.data;

      // 2) Link people
      if (selectedPeople.length > 0) {
        await axios.post(`${API_BASE}/api/compass_projects/${project.id}/members`, { people: selectedPeople.map(p => p.id) });
      }

      // 3) Link tags
      if (selectedTags.length > 0) {
        await axios.post(`${API_BASE}/api/compass_projects/${project.id}/tags`, { tags: selectedTags.map(t => t.id) });
      }

      // 4) Link proposals
      if (selectedProposals.length > 0) {
        await axios.post(`${API_BASE}/api/proposal_project_link`, { project_id: project.id, proposal_ids: selectedProposals.map(p => p.id) });
      }

      setSuccess("Project created successfully.");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to save project";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  // --- Populate with AI ---
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState("");
  const fileInputId = "populate-ai-file";

  async function populateWithAI(file) {
    setAiBusy(true); setAiMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API_BASE}/api/compass_projects/populate-ai`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data || {};
      await applyAiSuggestions(data);
      setAiMsg("AI suggestions applied. Review and adjust before saving.");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to populate with AI";
      setError(msg);
    } finally {
      setAiBusy(false);
    }
  }

  // Maps AI payload -> component state. Attempts to resolve IDs by name/slug/email.
  async function applyAiSuggestions(ai) {
    // Basic scalars
    if (ai.title) setTitle(ai.title);
    if (ai.project_number) setProjectNumber(ai.project_number);
    if (ai.status) setStatus(ai.status);
    if (ai.billable_amount != null) setBillableAmount(String(ai.billable_amount));
    if (ai.currency) setCurrency(ai.currency);
    if (ai.summary_start_date) setStartDate(new Date(ai.summary_start_date));
    if (ai.summary_end_date) setEndDate(new Date(ai.summary_end_date));
    if (ai.sharepoint_id) setSharepointId(ai.sharepoint_id);
    if (ai.folder_link) setFolderLink(ai.folder_link);
    if (ai.compass_pm_ref) setCompassPmRef(ai.compass_pm_ref);
    if (ai.team_members_ref) setTeamMembersRef(ai.team_members_ref);
    if (ai.client_friendly_name) setClientFriendlyName(ai.client_friendly_name);
    if (ai.canonical_description) setCanonicalDescription(ai.canonical_description);
    if (ai.search_text) setSearchText(ai.search_text);
    if (typeof ai.has_followup === "boolean") setHasFollowup(ai.has_followup);
    if (typeof ai.has_multiple_phases === "boolean") setHasMultiplePhases(ai.has_multiple_phases);
    if (typeof ai.client_retention === "boolean") setClientRetention(ai.client_retention);

    // Client resolution by name
    if (ai.client_name && !clientId) {
      try {
        const r = await axios.get(`${API_BASE}/api/clients`, { params: { q: ai.client_name } });
        const match = (r.data || []).find(c => c.name?.toLowerCase() === ai.client_name.toLowerCase()) || (r.data || [])[0];
        if (match?.id) setClientId(match.id);
        setClientSearch(ai.client_name);
      } catch {}
    }

    // People resolution by email or name
    if (Array.isArray(ai.people)) {
      let resolved = [];
      for (const p of ai.people) {
        try {
          const q =  p.name;
          if (!q) continue;
          const r = await axios.get(`${API_BASE}/api/people`, { params: { q } });
          const found = (r.data.data || []).find(x =>
            // (p.email && x.email?.toLowerCase() === p.email.toLowerCase()) ||
            (p.name && x.name?.toLowerCase() === p.name.toLowerCase())
          ) || (r.data.data || [])[0];
          if (found) resolved.push(found);
        } catch {}
      }
      if (resolved.length) setSelectedPeople(prev => {
        const map = new Map(prev.map(x => [x.id, x]));
        resolved.forEach(x => map.set(x.id, x));
        return Array.from(map.values());
      });
    }

    // Tags resolution by slug or name
    if (Array.isArray(ai.tags)) {
      let resolved = [];
      for (const t of ai.tags) {
        try {
          const q = t.slug || t.name;
          if (!q) continue;
          const r = await axios.get(`${API_BASE}/api/tags`, { params: { q } });
          const found = (r.data || []).find(x =>
            (t.slug && x.slug?.toLowerCase() === t.slug.toLowerCase()) ||
            (t.name && x.name?.toLowerCase() === t.name.toLowerCase())
          ) || (r.data || [])[0];
          if (found) resolved.push(found);
        } catch {}
      }
      if (resolved.length) setSelectedTags(prev => {
        const map = new Map(prev.map(x => [x.id, x]));
        resolved.forEach(x => map.set(x.id, x));
        return Array.from(map.values());
      });
    }

    // Proposals resolution by title (optional)
    if (Array.isArray(ai.proposals)) {
      let resolved = [];
      for (const p of ai.proposals) {
        try {
          const q = p.title || p.id;
          if (!q) continue;
          const r = await axios.get(`${API_BASE}/api/proposals`, { params: { q, limit: 10 } });
          const found = (r.data || []).find(x => (p.id && x.id === p.id) || (p.title && x.title?.toLowerCase() === p.title.toLowerCase())) || (r.data || [])[0];
          if (found) resolved.push(found);
        } catch {}
      }
      if (resolved.length) setSelectedProposals(prev => {
        const map = new Map(prev.map(x => [x.id, x]));
        resolved.forEach(x => map.set(x.id, x));
        return Array.from(map.values());
      });
    }
  }

  function onClickPopulateAi() {
    const input = document.getElementById(fileInputId);
    if (input) input.click();
  }

  function onFileChosen(e) {
    const f = e.target.files?.[0];
    if (f) populateWithAI(f);
    // reset input so the same file can be chosen again if needed
    e.target.value = "";
  }

  return (
    <Stack tokens={{ childrenGap: 20, padding: 20 }}>
      <SectionHeader title="Create New Project" sub="Add project details, assign people, tags, and link proposals." />
      <PrimaryButton iconProps={{iconName: "Robot"}} onClick={onClickPopulateAi} text="Populate With Ai" />

      {error && <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError("")}>{error}</MessageBar>}
      {success && <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccess("")}>{success}</MessageBar>}

      {/* Core fields */}
      <Stack tokens={{ childrenGap: 12 }}>
        <input id={fileInputId} type="file" accept=".pdf,.doc,.docx,.txt,.rtf" style={{ display: "none" }} onChange={onFileChosen} />
        <TextField label="Title" required value={title} onChange={(_, v) => setTitle(v || "")} errorMessage={titleError} />
        <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
          <TextField label="Project number" value={projectNumber} onChange={(_, v) => setProjectNumber(v || "")} styles={{ root: { width: 240 } }} />
          <Dropdown label="Status" required selectedKey={status} onChange={(_, opt) => setStatus(opt?.key)} options={statusOptions} styles={{ root: { width: 240 } }} />
          <Dropdown label="Currency" selectedKey={currency} onChange={(_, opt) => setCurrency(String(opt?.key))} options={currencyOptions} styles={{ root: { width: 160 } }} />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
          <TextField label="Billable amount" value={billableAmount} onChange={(_, v) => setBillableAmount(v || "")} styles={{ root: { width: 200 } }} />
          <DatePicker label="Start date" firstDayOfWeek={DayOfWeek.Sunday} value={startDate} onSelectDate={setStartDate} strings={dayPickerStrings} styles={{ root: { width: 220 } }} />
          <DatePicker label="End date" firstDayOfWeek={DayOfWeek.Sunday} value={endDate} onSelectDate={setEndDate} strings={dayPickerStrings} styles={{ root: { width: 220 } }} />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
          {/* <TextField label="SharePoint ID" value={sharepointId} onChange={(_, v) => setSharepointId(v || "")} styles={{ root: { width: 240 } }} /> */}
          <TextField label="Folder link" value={folderLink} onChange={(_, v) => setFolderLink(v || "")} styles={{ root: { width: 400 } }} />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
          <TextField label="Compass PM ref" value={compassPmRef} onChange={(_, v) => setCompassPmRef(v || "")} styles={{ root: { width: 260 } }} />
          <TextField label="Team members ref" value={teamMembersRef} onChange={(_, v) => setTeamMembersRef(v || "")} styles={{ root: { width: 260 } }} />
          {/* <TextField label="Client-friendly name" value={clientFriendlyName} onChange={(_, v) => setClientFriendlyName(v || "")} styles={{ root: { width: 300 } }} /> */}
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
          <Dropdown label="Client" placeholder="Select client (search below)" selectedKey={clientId || undefined} onChange={(_, opt) => setClientId(String(opt?.key))} options={clientOptions} styles={{ root: { width: 360 } }} />
          {/* <TextField label="Client search" placeholder="Type to find clients…" value={clientSearch} onChange={(_, v) => setClientSearch(v || "")} styles={{ root: { width: 280 } }} /> */}
        </Stack>
        <TextField label="Canonical description" multiline autoAdjustHeight rows={4} value={canonicalDescription} onChange={(_, v) => setCanonicalDescription(v || "")} />
        <TextField label="Search text" multiline autoAdjustHeight rows={3} value={searchText} onChange={(_, v) => setSearchText(v || "")} />
        {/* <Stack horizontal tokens={{ childrenGap: 24 }}>
          <Toggle label="Has follow‑up" checked={hasFollowup} onChange={(_, v) => setHasFollowup(!!v)} />
          <Toggle label="Multiple phases" checked={hasMultiplePhases} onChange={(_, v) => setHasMultiplePhases(!!v)} />
          <Toggle label="Client retention" checked={clientRetention} onChange={(_, v) => setClientRetention(!!v)} />
        </Stack> */}
      </Stack>

      <Separator />

      {/* People (dual list) */}
      <SectionHeader title="People" sub="Move items between lists to assign team members." />
      <PeopleDualList selected={selectedPeople} setSelected={setSelectedPeople} />

      <Separator />

      {/* Tags (dual list) */}
      <SectionHeader title="Tags" sub="Select from the right to add; remove from the left to unassign." />
      <TagsDualList selected={selectedTags} setSelected={setSelectedTags} />

      <Separator />

      {/* Proposals (search panel) */}
      <SectionHeader title="Linked Proposals" sub="Associate this project with relevant proposals." />
      <ProposalLinker selected={selectedProposals} onAdd={(p) => setSelectedProposals(prev => prev.find(x => x.id === p.id) ? prev : [...prev, p])} onRemove={(id) => setSelectedProposals(prev => prev.filter(x => x.id !== id))} />

      <Stack horizontal tokens={{ childrenGap: 12 }}>
        <PrimaryButton text={saving ? "Saving..." : "Create project"} onClick={handleSave} disabled={!canSave} />
        <DefaultButton text="Cancel" onClick={() => window.history.back()} />
      </Stack>
    </Stack>
  );
}